import { Component } from '@theme/component';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon with a live item count label.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartCount - The span holding the numeric cart count inside the label.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartCount.textContent = String(value);
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartCountIsCorrect();
    this.syncWithServer();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartCountIsCorrect();
      this.syncWithServer();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.updateCount(itemCount, comingFromProductForm);
  };

  /**
   * Updates the displayed cart count.
   * @param {number} itemCount - The number of items added or the new total.
   * @param {boolean} comingFromProductForm - Whether the update is an incremental add from the product form.
   */
  updateCount = (itemCount, comingFromProductForm) => {
    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );
  };

  /**
   * Fetches the live cart count from Shopify and updates the label if it differs from what is displayed.
   * This corrects stale values on CDN-cached pages (e.g. the homepage showing "(0)" despite items in cart).
   */
  syncWithServer = async () => {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      const itemCount = Number(cart.item_count) || 0;

      if (this.currentCartCount !== itemCount) {
        this.currentCartCount = itemCount;
      }

      sessionStorage.setItem(
        'cart-count',
        JSON.stringify({ value: String(itemCount), timestamp: Date.now() })
      );
    } catch (_) {
      // network errors are silent; the cached value remains visible
    }
  };

  /**
   * Checks session storage and corrects the displayed count if it drifted (e.g. after a back-navigation).
   */
  ensureCartCountIsCorrect = () => {
    if (!this.refs.cartCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (value === visibleCount) return;

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);
        if (count >= 0) {
          this.currentCartCount = count;
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
